import { useEffect, useMemo, useState } from "react";
import {
    Button, Card, Divider, Empty, InputNumber, Modal, Select, Space, Tag, Typography,
} from "antd";
import {
    UserOutlined, LinkOutlined, DisconnectOutlined, PhoneOutlined,
    FileSearchOutlined, DollarOutlined, ExclamationCircleFilled,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import axiosInstance from "../../../../../variables/api/axiosInstance";
import MandatInvestigationModal from "../modals/MandatInvestigationModal";

const { Title, Text } = Typography;
const { confirm } = Modal;
const PRIMARY = "#242166";
const SUCCESS = "#16a34a";

// Un investigateur est une Personne « expert » dont la profession contient INVESTIGAT.
const estInvestigateur = (p) => (p?.profession || "").toUpperCase().includes("INVESTIGAT");
const nomComplet = (p) => `${p?.nom || ""} ${p?.prenom || ""}`.trim();

/**
 * Onglet « Investigateurs » du dossier sinistre : affecte / détache DIRECTEMENT un ou PLUSIEURS
 * investigateurs (Personnes, profession INVESTIGATEUR), sans Expertise ni victime — calqué sur
 * l'onglet Conseil juridique mais en multi-affectation. Par investigateur : bâtir/imprimer le
 * Mandat d'investigation (points communs au dossier, nom de l'investigateur sur le doc) et un
 * règlement léger (honoraires + frais + retenue).
 * Endpoints : GET /api/expertise/expert/all,
 * POST /api/sinistre/investigateur/codeId/{sinistreId}/{expertCodeId},
 * DELETE /api/sinistre/investigateur/codeId/{sinistreId}/{expertCodeId},
 * POST /api/decaissement/all-experts.
 */
const InvestigateurTab = ({ sinistre, setRefetch }) => {
    const readOnly = !!sinistre?.lectureSeule;
    const [experts, setExperts] = useState([]);
    const [selected, setSelected] = useState(null);
    const [saving, setSaving] = useState(false);

    // Mandat d'investigation ciblé sur un investigateur précis (nom sur le document).
    const [mandatOpen, setMandatOpen] = useState(false);
    const [mandatFor, setMandatFor] = useState(null);

    // Règlement léger, ciblé sur un investigateur précis.
    const [payOpen, setPayOpen] = useState(false);
    const [payFor, setPayFor] = useState(null);
    const [honoraires, setHonoraires] = useState(0);
    const [fraisAccessoires, setFraisAccessoires] = useState(0);
    const [tauxRetenue, setTauxRetenue] = useState(5);
    const [paying, setPaying] = useState(false);

    // Liste des investigateurs affectés au dossier (plusieurs possibles).
    const investigateurs = useMemo(
        () => (Array.isArray(sinistre?.investigateurs) ? sinistre.investigateurs : []),
        [sinistre?.investigateurs]
    );

    useEffect(() => {
        axiosInstance.get("/api/expertise/expert/all")
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : (res.data?.content ?? []);
                setExperts(list.filter(estInvestigateur));
            })
            .catch((e) => { console.error(e); toast.error("Erreur lors du chargement des investigateurs."); });
    }, []);

    // Catalogue proposé à l'affectation, en excluant ceux déjà affectés.
    const options = useMemo(() => {
        const dejaCodes = new Set(investigateurs.map((p) => p?.codeId));
        return experts
            .filter((e) => !dejaCodes.has(e.codeId))
            .map((e) => ({
                value: e.codeId,
                label: nomComplet(e) || e.codeId,
                tel: e.telephone1 || e.telephone,
                profession: e.profession,
            }));
    }, [experts, investigateurs]);

    const refresh = () => setRefetch && setRefetch((p) => !p);

    const affecter = () => {
        if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
        if (!selected || !sinistre?.codeId) return;
        setSaving(true);
        axiosInstance.post(`/api/sinistre/investigateur/codeId/${sinistre.codeId}/${selected}`)
            .then(() => { toast.success("Investigateur affecté au dossier."); setSelected(null); refresh(); })
            .catch((e) => { console.error(e); toast.error("Erreur lors de l'affectation."); })
            .finally(() => setSaving(false));
    };

    const detacher = (inv) => {
        if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
        confirm({
            title: "Détacher cet investigateur ?",
            icon: <ExclamationCircleFilled />,
            content: nomComplet(inv),
            okText: "Détacher", okButtonProps: { danger: true }, cancelText: "Annuler",
            onOk: () => axiosInstance.delete(`/api/sinistre/investigateur/codeId/${sinistre.codeId}/${inv.codeId}`)
                .then(() => { toast.success("Investigateur détaché."); refresh(); })
                .catch((e) => { console.error(e); toast.error("Erreur lors du détachement."); }),
        });
    };

    const ouvrirMandat = (inv) => {
        if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
        setMandatFor(inv); setMandatOpen(true);
    };

    // ── Règlement léger (réutilise le flux de paiement des experts) ──
    const ouvrirPaiement = (inv) => {
        if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
        setPayFor(inv);
        setHonoraires(0);
        setFraisAccessoires(0);
        setTauxRetenue(5);
        setPayOpen(true);
    };

    const totalBrut = Number(honoraires || 0) + Number(fraisAccessoires || 0);
    // La retenue ne porte QUE sur les honoraires ; les frais accessoires sont payés à valeur faciale.
    const montantRetenue = Number(honoraires || 0) * (tauxRetenue / 100);
    const totalNet = totalBrut - montantRetenue;
    const fcfa = (n) => `${Number(n || 0).toLocaleString("fr-FR")} FCFA`;

    const enregistrerPaiement = () => {
        if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
        if (!payFor?.codeId || !sinistre?.codeId) return;
        if (totalBrut <= 0) { toast.error("Saisissez un montant à régler."); return; }
        setPaying(true);
        const payload = [{
            codeId: sinistre.codeId,
            honoraires: Number(honoraires || 0),
            fraisAccessoires: Number(fraisAccessoires || 0),
            retenue: tauxRetenue,
            expertCodeId: payFor.codeId,
        }];
        axiosInstance.post("/api/decaissement/all-experts", JSON.stringify(payload), {
            headers: { "Content-Type": "application/json" },
        })
            .then(() => { toast.success("Règlement de l'investigateur enregistré."); setPayOpen(false); refresh(); })
            .catch((e) => { console.error(e); toast.error("Erreur lors de l'enregistrement du règlement."); })
            .finally(() => setPaying(false));
    };

    return (
        <Card bordered={false} style={{ borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
                <FileSearchOutlined style={{ fontSize: 22, color: PRIMARY }} />
                <Title level={5} style={{ margin: 0, color: PRIMARY }}>Investigateurs du dossier</Title>
                <Tag color={investigateurs.length ? "processing" : "default"}>{investigateurs.length}</Tag>
            </div>

            {/* Investigateurs affectés */}
            {investigateurs.length > 0 ? (
                <Space direction="vertical" style={{ width: "100%", marginBottom: 22 }} size={12}>
                    {investigateurs.map((inv) => (
                        <div key={inv.codeId} style={{
                            padding: 16, borderRadius: 12, background: "#f5f5ff", border: "1px solid #e4e3ff",
                        }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
                                <div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <UserOutlined style={{ color: PRIMARY }} />
                                        <Text strong style={{ fontSize: 16, color: PRIMARY }}>{nomComplet(inv) || "—"}</Text>
                                        <Tag color="gold">Investigateur</Tag>
                                    </div>
                                    <Space direction="vertical" size={2} style={{ marginTop: 8 }}>
                                        {inv.profession && <Text type="secondary">{inv.profession}</Text>}
                                        {(inv.telephone1 || inv.telephone) && (
                                            <Text type="secondary"><PhoneOutlined /> {inv.telephone1 || inv.telephone}</Text>
                                        )}
                                    </Space>
                                </div>
                                <Space wrap>
                                    <Button icon={<FileSearchOutlined />} disabled={readOnly} onClick={() => ouvrirMandat(inv)}>Mandat d'investigation</Button>
                                    <Button icon={<DollarOutlined />} disabled={readOnly} onClick={() => ouvrirPaiement(inv)} style={{ borderColor: SUCCESS, color: SUCCESS }}>Régler</Button>
                                    <Button danger icon={<DisconnectOutlined />} disabled={readOnly} onClick={() => detacher(inv)}>Détacher</Button>
                                </Space>
                            </div>
                        </div>
                    ))}
                </Space>
            ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucun investigateur affecté à ce dossier" style={{ marginBottom: 22 }} />
            )}

            {/* Affecter un investigateur (ajout à la liste) */}
            <Text type="secondary" style={{ fontSize: 13 }}>Affecter un investigateur</Text>
            <div style={{ display: "flex", gap: 10, marginTop: 6, flexWrap: "wrap" }}>
                <Select
                    showSearch allowClear style={{ flex: 1, minWidth: 260 }}
                    placeholder="Rechercher un investigateur…" optionFilterProp="label"
                    value={selected} onChange={setSelected} options={options}
                    notFoundContent="Aucun investigateur (profession INVESTIGATEUR) disponible"
                    optionRender={(opt) => (
                        <div>
                            <div style={{ fontWeight: 600 }}>{opt.data.label}</div>
                            {(opt.data.profession || opt.data.tel) && (
                                <div style={{ fontSize: 12, color: "#9ca3af" }}>{[opt.data.profession, opt.data.tel].filter(Boolean).join(" · ")}</div>
                            )}
                        </div>
                    )}
                />
                <Button
                    type="primary" icon={<LinkOutlined />} loading={saving} disabled={!selected || readOnly}
                    onClick={affecter} style={{ background: selected ? PRIMARY : undefined, border: "none" }}
                >
                    Affecter
                </Button>
            </div>

            {/* Mandat d'investigation AU NIVEAU DU DOSSIER (points communs), nom = investigateur ciblé */}
            <MandatInvestigationModal
                open={mandatOpen}
                setOpen={setMandatOpen}
                sinistre={sinistre}
                sinistreMode
                investigateur={mandatFor}
                onSaved={refresh}
            />

            {/* Règlement léger de l'investigateur ciblé */}
            <Modal
                title={<span style={{ color: PRIMARY }}>Régler l'investigateur — {nomComplet(payFor)}</span>}
                open={payOpen}
                onCancel={() => setPayOpen(false)}
                onOk={enregistrerPaiement}
                confirmLoading={paying}
                okText="Enregistrer le règlement"
                cancelText="Annuler"
                okButtonProps={{ disabled: totalBrut <= 0, style: { background: totalBrut > 0 ? SUCCESS : undefined, border: "none" } }}
                centered
            >
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 8 }}>
                    <div>
                        <Text type="secondary" style={{ fontSize: 13 }}>Honoraires</Text>
                        <InputNumber
                            style={{ width: "100%", marginTop: 4 }} min={0} value={honoraires}
                            controls={false}
                            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
                            parser={(v) => `${v}`.replace(/\s/g, "")}
                            onChange={(v) => setHonoraires(Number(v) || 0)}
                        />
                    </div>
                    <div>
                        <Text type="secondary" style={{ fontSize: 13 }}>Frais accessoires</Text>
                        <InputNumber
                            style={{ width: "100%", marginTop: 4 }} min={0} value={fraisAccessoires}
                            controls={false}
                            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
                            parser={(v) => `${v}`.replace(/\s/g, "")}
                            onChange={(v) => setFraisAccessoires(Number(v) || 0)}
                        />
                    </div>
                    <div>
                        <Text type="secondary" style={{ fontSize: 13 }}>Taux de retenue</Text>
                        <Select
                            style={{ width: "100%", marginTop: 4 }} value={tauxRetenue} onChange={setTauxRetenue}
                            options={[
                                { value: 0, label: "0 %" },
                                { value: 3, label: "3 %" },
                                { value: 5, label: "5 %" },
                                { value: 20, label: "20 %" },
                            ]}
                        />
                    </div>
                </div>
                <Divider style={{ margin: "16px 0" }} />
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#6b7280" }}>
                    <span>Brut : <b>{fcfa(totalBrut)}</b></span>
                    <span>Retenue honoraires ({tauxRetenue}%) : <b style={{ color: "#dc2626" }}>-{fcfa(montantRetenue)}</b></span>
                    <span>Net : <b style={{ color: SUCCESS }}>{fcfa(totalNet)}</b></span>
                </div>
            </Modal>
        </Card>
    );
};

export default InvestigateurTab;
